import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Network, Stethoscope, FlaskConical, Pill, Building2, Ambulance, Shield, Users } from 'lucide-react';
import NHCIdBadge from '../components/NHCIdBadge';
import Spinner from '../../../components/ui/Spinner';
import { patientApi, type EcosystemOverview } from '../api';

/**
 * Who this patient is connected to across NeoHealthCard.
 *
 * The page used to set its list to an empty array with a note that there was
 * no backend for it. `GET /ecosystem` has been serving exactly this all along:
 * one node per provider type with the name, the NHC id and how many records
 * sit behind it. This account is connected to six.
 */

const ICONS: Record<string, typeof Network> = {
  DOCTOR: Stethoscope,
  LAB: FlaskConical,
  PHARMACY: Pill,
  HOSPITAL: Building2,
  AMBULANCE: Ambulance,
  INSURANCE: Shield,
  FAMILY: Users,
};

/** Where each connection is managed, so a node is a way in rather than a tile. */
const ROUTES: Record<string, string> = {
  DOCTOR: '/patient/appointments',
  LAB: '/patient/lab',
  PHARMACY: '/patient/pharmacy',
  HOSPITAL: '/patient/hospital',
  AMBULANCE: '/patient/ambulance',
  INSURANCE: '/patient/insurance',
  FAMILY: '/patient/family',
};

export default function EcosystemLinksPage() {
  const navigate = useNavigate();
  const [data, setData] = useState<EcosystemOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setData(await patientApi.ecosystem());
    } catch (e: any) {
      setError(e?.body?.message || 'Your connections could not be loaded — check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  if (loading) return <div className="flex justify-center py-20"><Spinner size="lg" /></div>;

  const nodes = data?.nodes ?? [];
  const connected = nodes.filter((n) => n.connected);

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Network className="w-6 h-6 text-teal-600" /> Your care network
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Everyone your NeoHealthCard is connected to, and how many records sit behind each
          </p>
        </div>
        {data?.patient?.nhcId && <NHCIdBadge nhcId={data.patient.nhcId} />}
      </div>

      {error && (
        <div className="bg-white rounded-xl border border-red-200 p-6 text-center">
          <p className="text-sm text-red-700 font-medium">{error}</p>
          <button
            onClick={() => void load()}
            className="mt-3 px-4 py-2 text-xs font-semibold text-white bg-teal-600 rounded-lg hover:bg-teal-700"
          >
            Retry
          </button>
        </div>
      )}

      {!error && connected.length === 0 ? (
        <div className="text-center py-16">
          <Network className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">No connections yet</p>
          <p className="text-sm text-gray-400 mt-1">
            A doctor, lab, pharmacy or hospital appears here once they hold a record against your card.
          </p>
        </div>
      ) : (
        <>
          <div className="bg-teal-50 border border-teal-200 rounded-2xl p-4">
            <p className="text-sm text-teal-900">
              <span className="font-bold">{data?.connectedCount ?? connected.length}</span> connections across the network.
              Each one can see only what you have consented to.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {connected.map((n) => {
              const Icon = ICONS[n.key] ?? Network;
              const route = ROUTES[n.key];
              return (
                <button
                  key={n.key}
                  onClick={() => route && navigate(route)}
                  disabled={!route}
                  className="bg-white border border-gray-200 rounded-2xl p-4 text-left hover:shadow-md transition-shadow disabled:cursor-default"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-teal-50 flex items-center justify-center shrink-0">
                      <Icon className="w-5 h-5 text-teal-600" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-semibold text-gray-900">{n.label}</p>
                        <span className="text-[10px] font-bold text-teal-700 bg-teal-50 rounded-full px-2 py-0.5">
                          {n.count}
                        </span>
                      </div>
                      {n.name && <p className="text-xs text-gray-700 mt-0.5 truncate">{n.name}</p>}
                      {n.detail && <p className="text-[11px] text-gray-500 mt-1 leading-relaxed">{n.detail}</p>}
                      {n.nhcId && <p className="text-[10px] text-gray-400 font-mono mt-1">{n.nhcId}</p>}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 text-[11px] text-gray-600 leading-relaxed">
            A connection is not access. What each provider can actually see is set by the consents you
            grant, which you can review and withdraw under Consent Center.
          </div>
        </>
      )}
    </div>
  );
}
